// T6 (LAUNCH_PLAN.md) — full-screen blocking modal that captures ToS+Privacy
// consent for existing users on next sign-in.
//
// Renders IN PLACE of any protected-route children (ProtectedRoute does the
// swap). The route's actual UI is not visible behind the modal — there is no
// blurred-content leak.
//
// Failure recovery (req #5: don't block forever):
//   - Network / callable failure → inline error + "Try again" button.
//   - Persistent failure → "Sign out" escape link → /sign-in?signed_out=consent_pending.
//   - No localStorage skip flag; consent is server-source-of-truth only.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { recordConsent } from '../lib/consent';
import { useCurrentUser } from '../contexts/UserContext';

export default function ConsentModal() {
  const navigate = useNavigate();
  const { refreshProfile } = useCurrentUser();
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAgree() {
    if (!agreed || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await recordConsent('consent_modal_existing_user');
      await refreshProfile();
      // No navigate() — refreshProfile flips hasConsent → ProtectedRoute
      // re-renders children naturally.
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Could not record your consent. Please try again.';
      console.error('[T6] recordConsent failed:', err);
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut() {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('[T6] signOut from consent modal failed:', err);
    }
    navigate('/sign-in?signed_out=consent_pending', { replace: true });
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="consent-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm px-4"
    >
      <div className="w-full max-w-lg bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
        <h2 id="consent-modal-title" className="text-xl font-semibold text-white mb-2">
          We've updated our terms
        </h2>
        <p className="text-sm text-gray-300 mb-6">
          ViewsBoost has updated its Terms of Service and Privacy Policy. Please review and confirm
          to continue.
        </p>

        <div className="space-y-2 mb-5">
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-purple-400 hover:text-purple-300 underline"
          >
            → Read the Terms of Service
          </a>
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-purple-400 hover:text-purple-300 underline"
          >
            → Read the Privacy Policy
          </a>
        </div>

        <label className="flex items-start gap-3 mb-5 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-1 accent-purple-500"
            disabled={submitting}
          />
          <span className="text-sm text-gray-200">
            I have read and agree to the Terms of Service and Privacy Policy.
          </span>
        </label>

        {error && (
          <div className="rounded-md border border-red-700 bg-red-900/40 px-3 py-2 text-sm text-red-300 mb-4">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={handleAgree}
            disabled={!agreed || submitting}
            className="w-full bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 rounded-lg transition-colors"
          >
            {submitting ? 'Saving…' : error ? 'Try again' : 'I agree and continue'}
          </button>

          <button
            type="button"
            onClick={handleSignOut}
            disabled={submitting}
            className="text-xs text-gray-400 hover:text-gray-200 underline self-center"
          >
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
